import type { Locale } from '../i18n'

export type PublicArticleSummary = {
  id: string
  locale: Locale
  slug: string
  title: string
  excerpt: string | null
  headerImageUrl: string | null
  publishedAt: string
  createdAt: string
  updatedAt: string
}

export type PublicArticleDetail = PublicArticleSummary & {
  body: string
}

export type PublicArticlesPage = {
  items: PublicArticleSummary[]
  pageInfo: {
    limit: number
    offset: number
    total: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

const configuredApiBase = import.meta.env.VITE_SIDEWISP_API_URL

export function sidewispApiBase() {
  if (configuredApiBase) return configuredApiBase
  if (import.meta.env.PROD) return 'https://api.sidewisp.com'
  return typeof window === 'undefined' ? 'http://127.0.0.1:3000' : window.location.origin
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(new URL(path, sidewispApiBase()), {
    headers: { accept: 'application/json' },
    cache: 'no-store',
    signal,
  })
  if (!response.ok) throw Object.assign(new Error(response.status === 404 ? 'article_not_found' : 'article_request_failed'), { status: response.status })
  return await response.json() as T
}

export const publicArticlesApi = Object.freeze({
  list: ({ locale = 'en', limit = 12, offset = 0 }: { locale?: Locale; limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
    request<PublicArticlesPage>(`/v1/articles?locale=${encodeURIComponent(locale)}&limit=${limit}&offset=${offset}`, signal),
  get: (slug: string, locale: Locale = 'en', signal?: AbortSignal) =>
    request<PublicArticleDetail>(`/v1/articles/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale)}`, signal),
})
